import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Trash2, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { BACKEND_URL } from '../lib/api';

const DeleteAccountConfirmPage = () => {
  const { token } = useParams();
  const [state, setState] = useState('idle'); // idle | deleting | done | error
  const [message, setMessage] = useState('');

  const handleDelete = async () => {
    setState('deleting');
    try {
      await axios.get(`${BACKEND_URL}/api/auth/confirm-delete-account/${encodeURIComponent(token)}`);
      // Clear local state
      try { localStorage.clear(); } catch {}
      setState('done');
      setMessage('Your VinePass account has been permanently deleted.');
    } catch (e) {
      setState('error');
      setMessage(e.response?.data?.detail || 'This link is invalid or has expired.');
    }
  };

  return (
    <div className="min-h-[80vh] bg-stone-50 py-16 px-4">
      <div className="max-w-md mx-auto bg-white border border-stone-200 rounded-2xl shadow-sm px-8 py-10 text-center">
        {state === 'idle' && (
          <>
            <div className="w-20 h-20 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-6">
              <Trash2 className="w-10 h-10 text-rose-700" strokeWidth={1.75} />
            </div>
            <h1 className="font-serif text-3xl text-[#1F3D2B] mb-3">Delete your account?</h1>
            <p className="text-stone-600 mb-2">This action is permanent. We will remove your account, your event listings, your tickets, and any associated orders.</p>
            <p className="text-sm text-stone-500 mb-8">There is no undo.</p>
            <button
              type="button"
              onClick={handleDelete}
              data-testid="delete-confirm-button"
              className="inline-block bg-rose-700 text-white px-6 py-3 rounded-full font-medium hover:bg-rose-800 transition-colors"
            >
              Yes, permanently delete my account
            </button>
            <div className="mt-4">
              <Link to="/" className="text-sm text-stone-500 hover:text-[#1F3D2B] hover:underline">
                Cancel — keep my account
              </Link>
            </div>
          </>
        )}
        {state === 'deleting' && (
          <div data-testid="delete-loading">
            <Loader2 className="w-12 h-12 text-stone-500 mx-auto mb-4 animate-spin" />
            <p className="text-stone-600">Deleting your account…</p>
          </div>
        )}
        {state === 'done' && (
          <div data-testid="delete-done">
            <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" strokeWidth={1.75} />
            </div>
            <h1 className="font-serif text-3xl text-[#1F3D2B] mb-3">Account Deleted</h1>
            <p className="text-stone-600 mb-8">{message}</p>
            <Link to="/" className="inline-block bg-[#1F3D2B] text-white px-6 py-3 rounded-full font-medium hover:bg-[#2a5239] transition-colors">
              Back to VinePass
            </Link>
          </div>
        )}
        {state === 'error' && (
          <div data-testid="delete-error">
            <div className="w-20 h-20 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-rose-500" strokeWidth={1.75} />
            </div>
            <h1 className="font-serif text-3xl text-[#1F3D2B] mb-3">Link Invalid</h1>
            <p className="text-stone-600 mb-8">{message}</p>
            <Link to="/" className="inline-block bg-[#1F3D2B] text-white px-6 py-3 rounded-full font-medium hover:bg-[#2a5239] transition-colors">
              Back to VinePass
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeleteAccountConfirmPage;
